import {useSession} from 'next-auth/react';
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import RootStore from '../Core/mobx/RootStore';
import {MQTTConnection} from '../lib/mqtt-connection';

type RealtimeContextType = {
    client: MQTTConnection | null;
    connectionStatus: 'connected' | 'disconnected' | 'connecting';
    subscriptionStatus: 'connected' | 'disconnected' | 'connecting';
};

type User = {
    name: string;
    email: string;
    image: string;
};

type Session = {
    data: {
        user: User;
        expires: string;
        id_token: string;
        roles: Array<'DEVELOPER' | 'AGENT' | 'MANAGER' | 'AP_MANAGER'>;
    };
    status: 'authenticated';
};

enum HookEvent {
    CALL_ANSWERED = 'CALL_ANSWERED',
    CALL_ENDED = 'CALL_ENDED',
}

enum Origin {
    KIXIE = 'KIXIE',
}

export type NormalizedWebhookData = {
    origin: Origin;
    clientEmail: string;
    agentEmail: string;
    clientPhoneNumber: string;
    answerDate: string;
    hookEvent: HookEvent;
};

export type CallEventType = {
    origin: string;
    answerDate: string;
    agentEmail: string;
    callId: string;
    clientEmail: string;
    clientPhoneNumber: string;
    crmLink: string;
    hubspotData: Record<string, {value: string}>;
    hookEvent: 'CALL_ANSWERED' | 'CALL_ENDED' | 'CALL_DIALED';
    walkthroughData: Record<string, unknown>;
    walkthroughState: Record<string, unknown>;
    persona?: string;
};

type RealtimeEvent = {
    properties: CallEventType;
};
export const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

type GlobalSubscription = {
    topic: string;
    handler: (topic: string, payload: any) => void;
};

export const RealtimeProvider = ({children}: {children: React.ReactNode}) => {
    const session = useSession() as unknown as Session;
    const history = useHistory();

    const [client, setClient] = useState<MQTTConnection | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>(
        'disconnected',
    );
    const [subscriptionStatus, setSubscriptionStatus] = useState<'connected' | 'disconnected' | 'connecting'>(
        'disconnected',
    );
    const [subscriptions] = useState<Map<string, (topic: string, payload: any) => void>>(new Map());

    // Define global subscriptions - these will be active as soon as connection is established
    const getGlobalSubscriptions = useCallback((): GlobalSubscription[] => {
        if (!session?.data?.user?.email) return [];

        const encodedEmail = Buffer.from(session.data.user.email).toString('hex');
        return [
            {
                topic: `platform-api-athena/${process.env.NEXT_PUBLIC_ATHENA_STAGE_NAME}/${encodedEmail}/live-tab`,
                handler: (_topic: string, payload: RealtimeEvent) => {
                    if (payload.properties.hookEvent === 'CALL_DIALED')
                        RootStore.uiStore.showSnackbar('Dialing. View call details.', 'info', 10000, {
                            label: 'View Call',
                            onClick: () => history.push(`/live-call`),
                        });
                    if (payload.properties.hookEvent === 'CALL_ANSWERED')
                        RootStore.uiStore.showSnackbar('Call connected. View call details.', 'success', 10000, {
                            label: 'View Call',
                            onClick: () => history.push(`/live-call`),
                        });

                    RootStore.liveTabStore.addMessage(payload.properties);
                },
            },
            // Add other global subscriptions here as needed
            // {
            //     topic: 'another/global/topic',
            //     handler: (topic, payload) => { ... }
            // }
        ];
    }, [session?.data?.user?.email]);

    // Handle initial connection and global subscriptions
    useEffect(() => {
        const mqttClient = new MQTTConnection(
            process.env.NEXT_PUBLIC_MQTT_ENDPOINT_URL as string,
            session.data.id_token,
        );

        const setupConnection = async () => {
            try {
                setConnectionStatus('connecting');
                await mqttClient.connect(session.data.id_token, session.data.user.email);
                setClient(mqttClient);
                setConnectionStatus('connected');

                // Set up global subscriptions
                const globalSubs = getGlobalSubscriptions();
                setSubscriptionStatus('connecting');
                for (const sub of globalSubs) {
                    try {
                        await mqttClient.subscribe(sub.topic, sub.handler);
                        subscriptions.set(sub.topic, sub.handler);
                    } catch (error) {
                        console.error(`Failed to set up global subscription for ${sub.topic}:`, error);
                    }
                }

                setSubscriptionStatus('connected');
                // Also resubscribe to any component-specific subscriptions
                for (const [topic, callback] of subscriptions.entries()) {
                    if (!globalSubs.some((gs) => gs.topic === topic)) {
                        await mqttClient.subscribe(topic, callback);
                    }
                }
            } catch (error) {
                console.error('MQTT connection failed:', error);
                setConnectionStatus('disconnected');
                setSubscriptionStatus('disconnected');
            }
        };

        if (session?.data?.user?.email) {
            setupConnection();
        }

        return () => {
            mqttClient.disconnect();
        };
    }, [session?.data?.user?.email]);

    // Component-specific subscription method
    const subscribe = useCallback(
        async (topic: string, callback: (topic: string, payload: any) => void) => {
            if (!client || !client.getConnection()) {
                setSubscriptionStatus('disconnected');

                throw new Error('MQTT client not connected');
            }

            try {
                setSubscriptionStatus('connecting');
                await client.subscribe(topic, callback);
                subscriptions.set(topic, callback);
                setSubscriptionStatus('connected');
            } catch (error) {
                console.error(`Failed to subscribe to ${topic}:`, error);
                throw error;
            }
        },
        [client],
    );

    useEffect(() => {
        if (client) client.token = session.data.id_token;
    }, [session.data.id_token]);

    const unsubscribe = useCallback(
        (topic: string) => {
            if (client && client.getConnection()) {
                client.unsubscribe(topic);
                subscriptions.delete(topic);
            }
        },
        [client],
    );

    const contextValue = useMemo(
        () => ({
            client,
            connectionStatus,
            subscriptionStatus,
            subscribe,
            unsubscribe,
        }),
        [client, connectionStatus, subscriptionStatus, subscribe, unsubscribe],
    );

    return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
};

export const useRealtime = (): RealtimeContextType => {
    const context = useContext(RealtimeContext);
    if (!context) {
        throw new Error('useMQTT must be used within an MQTTProvider');
    }
    return context;
};
